// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getProductInfoFromList,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import ProductListProductGridCard from "@store/Overrides/Widgets/ProductList/ProductListProductGridCard";
import { pxToRem, pxToRemArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { gridGapStyles } from "@toolkit/Themes/common/mixins";
import React, { ReactNode, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productRelation: string;
    emptyMessage: string;
    modalHeader: string;
    productId: string;
    modalIsOpen: boolean;
    onModalClose: () => void;
    extendedStyles?: RelatedProductsModalStyles;
}

const mapDispatchToProps = {
    loadCarouselProducts,
};

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const relatedProductsModalId = `${props.productRelation}-${props.productId}`.replace(/\s/g, "");

    return {
        state,
        products: getProductsForProductInfoList(state, relatedProductsModalId),
        pageType: getCurrentPage(state).type,
        relatedProductsModalId,
    };
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface RelatedProductsModalStyles {
    modal?: ModalPresentationProps;
    cardList?: CardListStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
    noContent?: InjectableCss;
    spinnerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const relatedProductsModalStyles: RelatedProductsModalStyles = {
    modal: {
        sizeVariant: "large",
        cssOverrides: {
            modalContent: css`
                background-color: ${getColor("common.accent")};
            `,
        },
    },
    cardList: {
        gridContainer: {
            gap: 0,
            css: gridGapStyles(pxToRemArray([20, 25, null, null, 30])),
        },
    },
    noContent: {
        css: css`
            padding: ${pxToRem(20)};
        `,
    },
    spinnerWrapper: {
        css: css`
            height: ${pxToRem(100)};
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
};

const RelatedProductsModal = ({
    productId,
    state,
    productRelation,
    modalHeader,
    emptyMessage,
    relatedProductsModalId,
    products,
    pageType,
    modalIsOpen,
    onModalClose,
    extendedStyles,
    loadCarouselProducts,
}: Props) => {
    const [styles] = useState(() => mergeToNew(relatedProductsModalStyles, extendedStyles));
    const [isLoading, setIsLoading] = useState(false);
    const isProductDetailsPage = pageType === constants.pageType.productDetails;
    const isProductListPage = pageType === constants.pageType.productList;
    const isBrandDetailsPage = pageType === constants.pageType.brandDetails;

    const modalCloseHandler = () => {
        onModalClose();
    };

    useEffect(() => {
        const carouselProps = {
            carouselId: relatedProductsModalId,
            carouselType: "relatedProducts",
            relatedProductType: productRelation,
            seedWithManuallyAssigned: "",
            displayProductsFrom: "allCategories",
            selectedCategoryIds: [],
            numberOfProductsToDisplay: 20,
            isProductDetailsPage,
            productId,
            isProductListPage,
            isBrandDetailsPage,
        };
        if (modalIsOpen) {
            setIsLoading(true);
            loadCarouselProducts(carouselProps);
        }
    }, [productId, productRelation, modalIsOpen]);

    useEffect(() => {
        if (products) {
            setIsLoading(false);
        }
    }, [products]);

    const renderProducts = (children: ReactNode) => {
        if (isLoading) {
            return (
                <StyledWrapper {...styles.spinnerWrapper}>
                    <LoadingSpinner {...styles.spinner} />
                </StyledWrapper>
            );
        }

        if (!products || products.length === 0) {
            return <StyledWrapper {...styles.noContent}>{emptyMessage}</StyledWrapper>;
        }

        return products.map((product, idx) => {
            if (!product) {
                return null;
            }
            const productInfo = getProductInfoFromList(state, relatedProductsModalId, product.id);

            if (!productInfo) {
                return null;
            }
            const productContext = {
                product,
                productInfo,
            };

            return (
                <ProductContext.Provider key={idx} value={productContext}>
                    {children}
                </ProductContext.Provider>
            );
        });
    };

    const overriddenProps = {
        showImage: true,
        showCompare: false,
        showBrand: true,
        showTitle: true,
        showPartNumbers: true,
        showAvailability: true,
        showAttributes: false,
        showPrice: true,
        showAddToList: true,
        productListDefaultView: "Grid",
        showImageBadges: false,
        maximumNumberOfImageBadges: 4,
        showTextBadges: true,
        maximumNumberOfTextBadges: 4,
        isModalContext: true,
    };

    return (
        <Modal {...styles.modal} headline={modalHeader} isOpen={modalIsOpen} handleClose={modalCloseHandler}>
            <CardList extendedStyles={styles.cardList} data-test-selector="productListCards_Grid">
                {renderProducts(
                    <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                        <ProductListProductGridCard {...overriddenProps} />
                    </CardContainerMultiColumn>,
                )}
            </CardList>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RelatedProductsModal);
